import React from "react";
import { motion } from "framer-motion";
import { Phone } from "lucide-react";
import EvaluationForm from "@/components/forms/EvaluationForm";
import { ConcentricMonogram } from "@/components/brand/ConcentricMonogram";
import { BRAND } from "@/data/brand";
import { AmbientLight } from "@/components/brand/AmbientLight";
import ChapterMark from "@/components/brand/ChapterMark";

export default function FinalConversion() {
  return (
    <section
      data-testid="final-conversion-section"
      className="relative py-24 sm:py-32 lg:py-52 overflow-hidden bg-midnight-gradient"
    >
      <AmbientLight size={1300} color="blue" intensity={0.55} style={{ top: "-10%", left: "10%" }} />
      <AmbientLight size={800} color="gold" intensity={0.22} style={{ bottom: "0%", right: "0%" }} />

      {/* Architectural backdrop */}
      <div
        className="hidden lg:block pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 opacity-30"
        aria-hidden
      >
        <ConcentricMonogram size={820} variant="ghost" glowColor="blue" rotateRings />
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        <ChapterMark index="13" label="Begin Your Evaluation" className="mb-14" />

        <div className="grid lg:grid-cols-12 gap-14 lg:gap-24">
          <div className="lg:col-span-5">
            <h2 className="font-display-tight text-white text-[clamp(2.5rem,5.6vw,5.25rem)] leading-[0.94] tracking-[-0.045em]">
              Start the conversation.
              <br />
              <span className="gold-metallic font-display font-light">No obligation.</span>
            </h2>
            <div className="mt-10 h-px w-16 bg-gradient-to-r from-gold to-transparent" />
            <p className="mt-10 text-ag-text-secondary text-base lg:text-[1.08rem] leading-[1.75] font-light max-w-md">
              Tell us about the property and the claim. The firm will review your file and recommend
              the appropriate next step. Engagement is contingency-based — no fee unless we recover.
            </p>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mt-10 sm:mt-12 lg:mt-16 pt-10 lg:pt-12 border-t border-white/[0.08]"
            >
              <a
                href={BRAND.phoneHref}
                data-testid="final-cta-call"
                className="flex items-center gap-6 text-white hover:text-gold transition-colors group"
              >
                <div className="w-14 h-14 rounded-full border border-gold/30 group-hover:border-gold flex items-center justify-center transition-all duration-500 group-hover:shadow-[0_0_30px_rgba(198,168,107,0.4)]">
                  <Phone className="w-4 h-4 text-gold" strokeWidth={1.4} />
                </div>
                <div>
                  <div className="font-mono text-[0.62rem] text-ag-text-muted tracking-[0.32em] uppercase mb-2">
                    Speak directly
                  </div>
                  <div className="font-display text-2xl lg:text-3xl tracking-[-0.025em] font-light">
                    {BRAND.phoneDisplay}
                  </div>
                </div>
              </a>
              <div className="mt-8 font-mono text-[0.6rem] text-ag-text-muted tracking-[0.3em] uppercase">
                {BRAND.licenseNumber}
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7 relative"
          >
            <EvaluationForm variant="embedded" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
